import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Routes users into the password-reset form when they arrive via a Supabase
 * recovery (forgot-password) link.
 *
 * The recovery link's `redirect_to` is the project's Site URL *root* (set by
 * Lovable Cloud's auth email layer), not `/reset-password`. So the link lands
 * on the homepage as `<site>/#access_token=...&type=recovery`. The Supabase
 * client parses and consumes that token (clearing the hash) and emits a
 * PASSWORD_RECOVERY event. With no global listener the user is simply stranded
 * on the homepage with no way to set a new password.
 *
 * This component lives at the app root (inside the router) and forwards the
 * user to the reset form. It covers two timing cases:
 *   1. The recovery marker captured at module load, before the client clears
 *      the URL hash (handles the case where the event fires before we subscribe).
 *   2. The PASSWORD_RECOVERY event, for the normal async case.
 */

// Captured at module-load time — runs synchronously during initial bundle
// evaluation, before the Supabase client's async init strips the URL hash.
const arrivedViaRecovery =
  typeof window !== "undefined" &&
  window.location.hash.includes("type=recovery");

// Forward to the reset form at most once per page load. PASSWORD_RECOVERY can
// fire more than once; without this guard the redirect could re-fire and, with
// any bounce on the reset page, produce a navigation loop.
let handled = false;

export const PasswordRecoveryRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const goToReset = () => {
      if (handled) return;
      handled = true;
      if (window.location.pathname !== "/reset-password") {
        navigate("/reset-password", { replace: true });
      }
    };

    if (arrivedViaRecovery) {
      goToReset();
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        goToReset();
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
};
